import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Breadcrumb } from '../../components/Breadcrumb';
import { Loader } from '../../components/Loader';
import { fetchContactById, fetchContacts } from '../../redux/contactStore/action';
import './ContactDetails.css';
import { fetchFiles, getFileById, uploadFile, deleteFile, updateFile } from '../../redux/fileStoreStore/action';
import { API } from '../../axios/instance';
import { fetchContactTypes } from '../../redux/contactTypeStore/action';
import { Modal } from '../../components/Modal';
import { FileUploadModal } from '../../components/FileUploadModal';
import { getUserContext } from '../../utils/auth';
import { AiOutlineFilePdf, AiOutlineFileImage, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFile } from 'react-icons/ai';
import { ContactInfoCard } from './cards/ContactInfoCard';
import { ContactFilesCard } from './cards/ContactFilesCard';
import { ContactNotesCard } from './cards/ContactNotesCard';
import { ContactLeadFlowCard } from './cards/ContactLeadFlowCard';
import { ContactTransactionsCard } from './cards/ContactTransactionsCard';
import { ContactTodosCard } from './cards/ContactTodosCard';

export function ContactDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const contactList = useSelector((s) => s.contact?.data);
  const currentContact = useSelector((s) => s.contact?.current);
  const contact = useMemo(() => {
    const fromList = contactList?.find?.(c => c._id === id);
    return fromList || currentContact || null;
  }, [contactList, currentContact, id]);
  const contactStatus = useSelector((s) => s.contact?.status);
  const rawFiles = useSelector((s) => s.fileStore?.data);
  const files = useMemo(() => rawFiles || [], [rawFiles]);
  const filesStatus = useSelector((s) => s.fileStore?.status);
  const rawContactTypes = useSelector((s) => s.contactType?.data);
  const contactTypes = useMemo(() => rawContactTypes || [], [rawContactTypes]);

  // File-related UI state
  const [showUpload, setShowUpload] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [preview, setPreview] = useState({ open: false, url: '', type: '', name: '' });
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editDraft, setEditDraft] = useState({ id: null, fileName: '' });

  const userContext = useMemo(() => getUserContext(), []);
  const [activeTab, setActiveTab] = useState('info');

  // Notes (backed by API)
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [expandedNoteIds, setExpandedNoteIds] = useState(new Set());
  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState('');
  // Referral chain state
  const [refChain, setRefChain] = useState([]);
  const [refLoading, setRefLoading] = useState(false);
  const [refError, setRefError] = useState('');
  const [editChainMode, setEditChainMode] = useState(false);
  const [chainDraft, setChainDraft] = useState([]);
  const [refSaveLoading, setRefSaveLoading] = useState(false);
  const [refSaveMsg, setRefSaveMsg] = useState('');
  const [refSaveErr, setRefSaveErr] = useState('');
  // Invoices (transactions)
  const [invoices, setInvoices] = useState([]);
  const [invLoading, setInvLoading] = useState(false);
  const [invError, setInvError] = useState('');
  const [showNewInv, setShowNewInv] = useState(false);
  const [invSaving, setInvSaving] = useState(false);
  const [invSaveErr, setInvSaveErr] = useState('');
  const [invForm, setInvForm] = useState({ type: 'income', amount: '', date: '', description: '' });
  const [pendingInvoicePhotos, setPendingInvoicePhotos] = useState([]);
  const [showInvoicePhotoModal, setShowInvoicePhotoModal] = useState(false);
  const [invoicePreviews, setInvoicePreviews] = useState({});
  // Todos
  const [todos, setTodos] = useState([]);
  const [todoLoading, setTodoLoading] = useState(false);
  const [todoError, setTodoError] = useState('');
  const [todoTitle, setTodoTitle] = useState('');
  const [todoDesc, setTodoDesc] = useState('');
  const [todoDue, setTodoDue] = useState('');

  useEffect(() => {
    let active = true;
    const load = async () => {
      setTodoLoading(true);
      setTodoError('');
      try {
        const res = await API.get('todo/todo', { params: { contact: id } });
        if (active) setTodos(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (active) setTodoError('Failed to load todos');
      } finally {
        if (active) setTodoLoading(false);
      }
    };
    if (id) load();
    return () => { active = false; };
  }, [id]);

  const addTodo = async () => {
    const title = (todoTitle || '').trim();
    if (!title || !contact) return;
    try {
      const payload = {
        userId: contact.userId || '6897acb7f2bc1479c645102d',
        businessId: contact.businessId || '6897b362dce30c2c9892b759',
        contactId: id,
        title,
        description: todoDesc || '',
        dueDate: todoDue || undefined,
      };
      const res = await API.post('todo/todo', payload);
      const created = res?.data?.result;
      if (created) setTodos((prev)=> [created, ...prev]);
      setTodoTitle(''); setTodoDesc(''); setTodoDue('');
    } catch (e) {
      alert('Failed to add todo');
    }
  };

  const toggleTodo = async (t) => {
    try {
      const res = await API.put(`todo/todo/${t._id}`, { isDone: !t.isDone });
      const updated = res?.data?.result;
      if (updated) setTodos((prev)=> prev.map(x => x._id === updated._id ? updated : x));
    } catch (e) { /* ignore */ }
  };

  const deleteTodo = async (t) => {
    if (!window.confirm('Delete this todo?')) return;
    try {
      await API.delete(`todo/todo/${t._id}`);
      setTodos((prev)=> prev.filter(x => x._id !== t._id));
    } catch (e) { /* ignore */ }
  };
  const balanceBDT = useMemo(() => {
    try {
      return (invoices || []).reduce((sum, inv) => {
        const cur = String(inv.currency || 'BDT').toUpperCase();
        const amt = Number(inv.amount || 0) || 0;
        if (cur !== 'BDT') return sum; // only count BDT for the header balance
        return sum + (inv.type === 'expense' ? -amt : amt);
      }, 0);
    } catch { return 0; }
  }, [invoices]);

  // Load notes for this contact from backend
  useEffect(() => {
    let active = true;
    const load = async () => {
      setNotesLoading(true);
      setNotesError('');
      try {
        const res = await API.get('note/note', { params: { contact: id } });
        if (active) setNotes(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        console.warn('Failed to load notes', e);
        if (active) setNotesError('Failed to load notes');
      } finally {
        if (active) setNotesLoading(false);
      }
    };
    if (id) load();
    return () => { active = false; };
  }, [id]);

  useEffect(() => {
    // Ensure contact details loaded
    dispatch(fetchContactById(id));
    // Load files associated to this contact
    dispatch(fetchFiles({ contact: id }));
    // Ensure we have contact types to resolve names
    dispatch(fetchContactTypes());
    // Ensure contacts list exists for chain editor options
    if (!Array.isArray(contactList) || contactList.length === 0) {
      dispatch(fetchContacts({}));
    }
  }, [dispatch, id, contactList]);

  const contactTypeLabel = useMemo(() => {
    const ct = contact?.contactType;
    if (!ct) return '-';
    const resolveId = (value) => {
      if (!value) return null;
      if (typeof value === 'string') return value.trim() || null;
      if (typeof value === 'object') return value._id || value.id || null;
      return null;
    };
    const resolveName = (value) => {
      if (!value) return null;
      if (typeof value === 'object' && value.contactTypeName) return value.contactTypeName;
      const id = resolveId(value);
      if (!id) return null;
      return contactTypes.find(t => t._id === id)?.contactTypeName || null;
    };
    const names = Array.isArray(ct)
      ? ct.map(resolveName).filter(Boolean)
      : [resolveName(ct)].filter(Boolean);
    return names.length > 0 ? names.join(', ') : '-';
  }, [contact, contactTypes]);

  const [referrerContact, setReferrerContact] = useState(null);
  const referredById = useMemo(() => {
    const ref = contact?.referredBy;
    if (!ref) return null;
    if (typeof ref === 'string') return ref;
    return ref._id || ref.id || null;
  }, [contact?.referredBy]);
  useEffect(() => {
    let active = true;
    const ref = contact?.referredBy;
    if (!ref) {
      setReferrerContact(null);
      return;
    }
    const asObject = typeof ref === 'object' ? ref : null;
    if (asObject && (asObject.contactName || asObject.contactPhone || asObject.contactEmail)) {
      setReferrerContact(asObject);
      return;
    }
    if (!referredById) {
      setReferrerContact(null);
      return;
    }
    const fromList = contactList?.find?.((c) => String(c._id) === String(referredById));
    if (fromList) {
      setReferrerContact(fromList);
      return;
    }
    const load = async () => {
      try {
        const res = await API.get(`contact/contact/${referredById}`);
        if (active) setReferrerContact(res.data || null);
      } catch (err) {
        if (active) setReferrerContact(null);
      }
    };
    load();
    return () => { active = false; };
  }, [contact?.referredBy, referredById, contactList]);

  const referrerLabel = useMemo(() => {
    const formatEntry = (entry) => {
      if (!entry) return null;
      if (typeof entry === 'string') return entry;
      return entry.contactName || entry.contactPhone || entry.contactEmail || (entry._id ? `Contact #${String(entry._id).slice(-6)}` : null);
    };
    const formatted = formatEntry(referrerContact) || formatEntry(contact?.referredBy);
    if (formatted) return formatted;
    return referredById ? `Contact #${String(referredById).slice(-6)}` : '-';
  }, [contact, referrerContact, referredById]);

  const formatDate = (iso) => {
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  const formatSize = (bytes) => {
    if (!bytes && bytes !== 0) return '-';
    const units = ['B','KB','MB','GB','TB'];
    let i = 0; let b = Math.max(bytes, 0);
    while (b >= 1024 && i < units.length - 1) { b /= 1024; i++; }
    return `${b.toFixed(b < 10 && i > 0 ? 1 : 0)} ${units[i]}`;
  };

  const getTypeFromName = (name = '') => {
    const n = name.toLowerCase();
    if (/(\.png|\.jpg|\.jpeg|\.gif|\.webp|\.svg)$/.test(n)) return 'image';
    if (/(\.pdf)$/.test(n)) return 'pdf';
    if (/(\.doc|\.docx)$/.test(n)) return 'doc';
    if (/(\.xls|\.xlsx|\.csv)$/.test(n)) return 'sheet';
    return 'other';
  };
  const normalizeType = (fileType, name = '') => {
    const ft = String(fileType || '').toLowerCase();
    if (ft.startsWith('image/')) return 'image';
    if (ft === 'application/pdf' || ft.endsWith('/pdf')) return 'pdf';
    if (ft.includes('word') || ft === 'application/msword' || ft.includes('officedocument.word')) return 'doc';
    if (ft.includes('excel') || ft.includes('spreadsheet') || ft.endsWith('/csv') || ft.includes('sheet')) return 'sheet';
    return getTypeFromName(name);
  };
  const TypeIcon = ({ type }) => {
    switch (type) {
      case 'pdf': return <AiOutlineFilePdf className="text-danger" size={18} />;
      case 'image': return <AiOutlineFileImage className="text-info" size={18} />;
      case 'doc': return <AiOutlineFileWord className="text-primary" size={18} />;
      case 'sheet': return <AiOutlineFileExcel className="text-success" size={18} />;
      default: return <AiOutlineFile size={18} />;
    }
  };

  // Load invoices for this contact
  useEffect(() => {
    let active = true;
    const load = async () => {
      setInvLoading(true);
      setInvError('');
      try {
        const res = await API.get('invoice/invoice', { params: { contact: id } });
        if (active) setInvoices(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (active) setInvError('Failed to load transactions');
      } finally {
        if (active) setInvLoading(false);
      }
    };
    if (id) load();
    return () => { active = false; };
  }, [id]);

  // Load referral chain for this contact
  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!id) return;
      setRefLoading(true);
      setRefError('');
      try {
        const res = await API.get(`contact/contact/${id}/referral-chain`, { params: { max: 10 } });
        if (active) setRefChain(Array.isArray(res.data) ? res.data : []);
      } catch (e) {
        if (active) setRefError('Failed to load referral chain');
      } finally {
        if (active) setRefLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, [id, referredById]);

  // Keep draft synced with current chain when editor opens
  useEffect(() => {
    if (editChainMode) setChainDraft((refChain || []).map(c => c._id));
  }, [editChainMode, refChain]);

  const saveReferralChain = async () => {
    setRefSaveLoading(true);
    setRefSaveErr('');
    setRefSaveMsg('');
    try {
      const cleaned = chainDraft.filter(Boolean);
      await API.put(`contact/contact/${id}/referral-chain`, { chain: cleaned });
      setRefSaveMsg('Saved');
      const res = await API.get(`contact/contact/${id}/referral-chain`, { params: { max: 10 } });
      setRefChain(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      setRefSaveErr('Failed to save');
    } finally {
      setRefSaveLoading(false);
      setTimeout(() => { setRefSaveMsg(''); setRefSaveErr(''); }, 1500);
    }
  };

  const addNote = async () => {
    const text = newNote.trim();
    if (!text || !contact) return;
    try {
      const payload = {
        userId: contact.userId || '6897acb7f2bc1479c645102d',
        businessId: contact.businessId || '6897b362dce30c2c9892b759',
        contactId: id,
        text,
      };
      const res = await API.post('note/note', payload);
      const created = res?.data?.result;
      if (created) setNotes((prev) => [created, ...prev]);
      setNewNote('');
    } catch (e) {
      console.error('Failed to add note', e);
      alert('Failed to add note');
    }
  };
  

  const deleteNote = async (nid) => {
    const ok = window.confirm('Delete this note?');
    if (!ok) return;
    try {
      await API.delete(`note/note/${nid}`);
      setNotes((prev) => prev.filter((n) => n._id !== nid));
      setExpandedNoteIds((prev) => { const next = new Set(prev); next.delete(nid); return next; });
    } catch (e) {
      console.error('Failed to delete note', e);
      alert('Failed to delete note');
    }
  };

  const toggleExpand = (nid) => {
    setExpandedNoteIds((prev) => {
      const next = new Set(prev);
      if (next.has(nid)) next.delete(nid); else next.add(nid);
      return next;
    });
  };

  const needsClamp = (text) => (text || '').length > 120;

  

  const handleDownload = async (file) => {
    try {
      const res = await dispatch(getFileById(file._id || file.id)).unwrap();
      const downloadUrl = res.downloadUrl || res.fileUrl;
      if (!downloadUrl) throw new Error('File download is not available');
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.target = '_blank';
      link.rel = 'noopener';
      link.download = res.fileName || file.fileName || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (e) {
      console.error('Download failed', e);
      alert('Download failed');
    }
  };

  // File actions (upload / preview / edit / delete)
  const handleFileInput = (e) => {
    const fList = Array.from(e.target.files || []);
    setSelectedFiles(fList);
  };

  const toBase64 = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1] || String(reader.result));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleUpload = async () => {
    try {
      setIsUploading(true);
      const effectiveUserId = contact?.userId || userContext.userId || '6897acb7f2bc1479c645102d';
      const effectiveBusinessId = contact?.businessId || userContext.businessId || '6897b362dce30c2c9892b759';
      if (!effectiveUserId || !effectiveBusinessId) throw new Error('Missing user/business context');
      for (const f of selectedFiles) {
        const base64 = await toBase64(f);
        await dispatch(uploadFile({
          userId: effectiveUserId,
          businessId: effectiveBusinessId,
          fileName: f.name,
          fileType: f.type,
          fileSize: f.size,
          dataBase64: base64,
          contactIds: [id],
        })).unwrap();
      }
      setSelectedFiles([]);
      setShowUpload(false);
      dispatch(fetchFiles({ contact: id }));
    } catch (e) {
      // Surface server error if available
      const msg = e?.response?.data?.message || e?.message || 'Upload failed';
      console.error('Upload failed', e);
      alert(msg);
    } finally {
      setIsUploading(false);
    }
  };

  const handlePreview = async (file) => {
    try {
      setIsPreviewLoading(true);
      const type = normalizeType(file.fileType || file.type, file.fileName || file.name);
      setPreview({ open: true, url: '', type, name: file.fileName || file.name });
      const res = await dispatch(getFileById(file._id || file.id)).unwrap();
      const url = res.downloadUrl || res.fileUrl;
      if (!url) throw new Error('Preview not available');
      setPreview({ open: true, url, type, name: file.fileName || file.name });
    } catch (e) {
      console.error('Preview failed', e);
      setPreview((p) => ({ ...p, url: '' }));
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const ensureInvoicePreview = async (fileId) => {
    if (!fileId) return null;
    const cached = invoicePreviews[fileId];
    if (cached && cached.url) return cached;
    try {
      const res = await dispatch(getFileById(fileId)).unwrap();
      const url = res.downloadUrl || res.fileUrl;
      const type = normalizeType(res.fileType, res.fileName);
      const record = { url, type, name: res.fileName || 'invoice-photo' };
      setInvoicePreviews(prev => ({ ...prev, [fileId]: record }));
      return record;
    } catch (e) {
      console.error('Failed to load invoice photo', e);
      return null;
    }
  };
  const handleDeleteFile = async (file) => {
    if (!window.confirm('Delete this file?')) return;
    try {
      await dispatch(deleteFile(file._id || file.id)).unwrap();
      dispatch(fetchFiles({ contact: id }));
    } catch (e) {
      console.error('Delete failed', e);
      alert('Delete failed');
    }
  };

  const openEdit = (file) => {
    setEditDraft({ id: file._id || file.id, fileName: file.fileName || file.name || '' });
    setIsEditOpen(true);
  };
  const saveEdit = async () => {
    try {
      await dispatch(updateFile({ id: editDraft.id, data: { fileName: editDraft.fileName } })).unwrap();
      setIsEditOpen(false);
      dispatch(fetchFiles({ contact: id }));
    } catch (e) {
      console.error('Update failed', e);
      alert('Update failed');
    }
  };

  const uploadInvoicePhotos = async () => {
    if (!pendingInvoicePhotos.length) return [];
    const effectiveUserId = contact?.userId || userContext.userId || '6897acb7f2bc1479c645102d';
    const effectiveBusinessId = contact?.businessId || userContext.businessId || '6897b362dce30c2c9892b759';
    if (!effectiveUserId || !effectiveBusinessId) return [];
    const uploadedIds = [];
    for (const f of pendingInvoicePhotos) {
      const base64 = await toBase64(f);
      const result = await dispatch(uploadFile({
        userId: effectiveUserId,
        businessId: effectiveBusinessId,
        fileName: f.name,
        fileType: f.type,
        fileSize: f.size,
        dataBase64: base64,
        contactIds: [id],
      })).unwrap();
      const fileId = result?._id || result?.id;
      if (fileId) uploadedIds.push(fileId);
    }
    return uploadedIds;
  };

  const handleSaveTransaction = async () => {
    setInvSaving(true); setInvSaveErr('');
    try {
      const invoicePhotoIds = await uploadInvoicePhotos();
      const payload = {
        userId: contact?.userId || '6897acb7f2bc1479c645102d',
        businessId: contact?.businessId || '6897b362dce30c2c9892b759',
        contactId: id,
        type: invForm.type,
        amount: parseFloat(invForm.amount),
        currency: 'BDT',
        date: invForm.date || undefined,
        description: invForm.description || '',
        photoFiles: invoicePhotoIds,
      };
      const res = await API.post('invoice/invoice', payload);
      const created = res?.data?.result;
      if (created) {
        setInvoices(prev => [created, ...prev]);
        setPendingInvoicePhotos([]);
      }
      setShowNewInv(false);
      setInvForm({ type: 'income', amount: '', date: '', description: '' });
    } catch (e) {
      setInvSaveErr('Failed to save transaction');
      console.error('Failed to save transaction', e);
    } finally {
      setInvSaving(false);
    }
  };

  return (
    <div className="container">
      <div className="d-flex align-items-center justify-content-between">
        <Breadcrumb items={[{ label: 'Home', path: '/dashboard' }, { label: 'Contacts', path: '/contact' }, { label: 'Details' }]} />
        {(contactStatus === 'loading' || filesStatus === 'loading') ? <Loader size="sm" /> : null}
      </div>

      <ul className="nav nav-tabs mt-3">
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'info' ? 'active' : ''}`} onClick={() => setActiveTab('info')}>Info</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'files' ? 'active' : ''}`} onClick={() => setActiveTab('files')}>Files</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'notes' ? 'active' : ''}`} onClick={() => setActiveTab('notes')}>Notes</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'lead' ? 'active' : ''}`} onClick={() => setActiveTab('lead')}>Lead Flow</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'tx' ? 'active' : ''}`} onClick={() => setActiveTab('tx')}>Transactions</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${activeTab === 'todos' ? 'active' : ''}`} onClick={() => setActiveTab('todos')}>Todos</button>
        </li>
      </ul>

      <div className="mt-3">
        {activeTab === 'info' && (
          <ContactInfoCard
            contact={contact}
            contactTypeLabel={contactTypeLabel}
            referrerLabel={referrerLabel}
            onBack={() => navigate('/contact')}
          />
        )}
        {activeTab === 'files' && (
          <ContactFilesCard
            files={files}
            normalizeType={normalizeType}
            formatSize={formatSize}
            TypeIcon={TypeIcon}
            onPreview={handlePreview}
            onDownload={handleDownload}
            onEdit={openEdit}
            onDelete={handleDeleteFile}
            onUploadClick={() => setShowUpload(true)}
          />
        )}
        {activeTab === 'notes' && (
          <ContactNotesCard
            notes={notes}
            notesLoading={notesLoading}
            notesError={notesError}
            newNote={newNote}
            setNewNote={setNewNote}
            addNote={addNote}
            deleteNote={deleteNote}
            expandedNoteIds={expandedNoteIds}
            toggleExpand={toggleExpand}
            needsClamp={needsClamp}
            formatDate={formatDate}
            canAdd={!!contact}
          />
        )}
        {activeTab === 'lead' && (
          <ContactLeadFlowCard
            refChain={refChain}
            refLoading={refLoading}
            refError={refError}
            editChainMode={editChainMode}
            setEditChainMode={setEditChainMode}
            chainDraft={chainDraft}
            setChainDraft={setChainDraft}
            saveReferralChain={saveReferralChain}
            refSaveLoading={refSaveLoading}
            refSaveMsg={refSaveMsg}
            refSaveErr={refSaveErr}
            contactList={contactList}
            currentId={id}
            navigate={navigate}
          />
        )}
        {activeTab === 'tx' && (
          <ContactTransactionsCard
            invoices={invoices}
            invLoading={invLoading}
            invError={invError}
            showNewInv={showNewInv}
            setShowNewInv={setShowNewInv}
            invSaving={invSaving}
            invSaveErr={invSaveErr}
            setInvSaveErr={setInvSaveErr}
            invForm={invForm}
            setInvForm={setInvForm}
            balanceBDT={balanceBDT}
            onSaveTransaction={handleSaveTransaction}
            pendingAttachments={pendingInvoicePhotos}
            onOpenAttachmentModal={()=> setShowInvoicePhotoModal(true)}
            onRemoveAttachment={(idx)=> setPendingInvoicePhotos(prev => prev.filter((_, i) => i !== idx))}
            onClearAttachments={()=> setPendingInvoicePhotos([])}
            onViewInvoicePhoto={async (files = []) => {
              if (!files || files.length === 0) return;
              const first = files[0];
              const record = await ensureInvoicePreview(
                typeof first === 'object' ? (first._id || first.id) : first
              );
              if (record && record.url) {
                setPreview({ open: true, url: record.url, type: record.type, name: record.name });
              } else {
                alert('Unable to open invoice photo.');
              }
            }}
          />
        )}
        {activeTab === 'todos' && (
          <ContactTodosCard
            todos={todos}
            loading={todoLoading}
            error={todoError}
            newTitle={todoTitle}
            setNewTitle={setTodoTitle}
            newDesc={todoDesc}
            setNewDesc={setTodoDesc}
            newDue={todoDue}
            setNewDue={setTodoDue}
            onAdd={addTodo}
            onToggle={toggleTodo}
            onDelete={deleteTodo}
          />
        )}
      </div>

      {/* Notes (moved to tabs)
      <div className="row g-3 mt-3">
        <div className="col-12">
          <div className="card h-100">
            <div className="card-header d-flex align-items-center justify-content-between">
              <h5 className="mb-0">Notes</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">New Note</label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Write a new note..."
                  value={newNote}
                  onChange={(e) => setNewNote(e.target.value)}
                />
                <div className="mt-2 d-flex justify-content-between align-items-center">
                  <div className="text-muted small">
                    {notesLoading ? 'Loading notes…' : notesError || ''}
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={addNote}
                    disabled={!newNote.trim() || !contact}
                  >
                    Add Note
                  </button>
                </div>
              </div>

              {notes.length === 0 ? (
                <div className="text-muted">No notes added yet.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '60%' }}>Note</th>
                        <th style={{ width: '30%' }}>Submitted</th>
                        <th style={{ width: '10%' }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {notes.map((n) => (
                        <tr key={n._id}>
                          <td style={{ whiteSpace: 'pre-wrap' }}>
                            <div className={expandedNoteIds.has(n._id) ? '' : 'line-clamp-2'}>{n.text}</div>
                            {needsClamp(n.text) && (
                              <button
                                type="button"
                                className="btn btn-link btn-sm p-0 mt-1"
                                onClick={() => toggleExpand(n._id)}
                              >
                                {expandedNoteIds.has(n._id) ? 'Read less' : 'Read more…'}
                              </button>
                            )}
                          </td>
                          <td>{formatDate(n.submittedAt || n.createdAt)}</td>
                          <td>
                            <button
                              className="btn btn-sm btn-outline-danger"
                              onClick={() => deleteNote(n._id)}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Transactions (moved to tabs)
      <div className="row g-3 mt-3"> 
        <div className="col-12">
          <div className="card h-100">
            <div className="card-header d-flex align-items-center justify-content-between"> 
              <h5 className="mb-0">Transactions</h5>
              <div className="d-flex align-items-center gap-2">
                {invLoading ? <Loader size="sm" /> : null}
                <span className="badge bg-light text-dark">Balance: BDT {Number(balanceBDT || 0).toLocaleString()}</span>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => { setShowNewInv(v => !v); setInvSaveErr(''); }}
                >
                  {showNewInv ? 'Close' : 'New Transaction'}
                </button>
              </div>
            </div>
            <div className="card-body">
              {showNewInv && (
                <div className="border rounded p-2 mb-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-sm-2">
                      <label className="form-label mb-1">Type</label>
                      <select className="form-select" value={invForm.type}
                        onChange={(e)=>setInvForm(f=>({...f, type: e.target.value}))}>
                        <option value="income">Income</option>
                        <option value="expense">Expense</option>
                      </select>
                    </div>
                    <div className="col-sm-2">
                      <label className="form-label mb-1">Amount</label>
                      <input className="form-control" type="number" min="0" step="0.01"
                        value={invForm.amount}
                        onChange={(e)=>setInvForm(f=>({...f, amount: e.target.value}))}
                        placeholder="0.00" />
                    </div>
                    
                    <div className="col-sm-3">
                      <label className="form-label mb-1">Date</label>
                      <input className="form-control" type="date" value={invForm.date}
                        onChange={(e)=>setInvForm(f=>({...f, date: e.target.value}))} />
                    </div>
                    <div className="col-sm-12">
                      <label className="form-label mb-1">Description</label>
                      <textarea className="form-control" rows={2}
                        value={invForm.description}
                        onChange={(e)=>setInvForm(f=>({...f, description: e.target.value}))}
                        placeholder="Optional details" />
                    </div>
                    {invSaveErr && (
                      <div className="col-sm-12 text-danger small">{invSaveErr}</div>
                    )}
                    <div className="col-sm-12 d-flex justify-content-end gap-2">
                      <button className="btn btn-outline-secondary" onClick={()=>{ setShowNewInv(false); setInvSaveErr(''); }}>Cancel</button>
                      <button className="btn btn-success" disabled={invSaving || !invForm.type || !(parseFloat(invForm.amount) > 0)}
                        onClick={async ()=>{
                          setInvSaving(true); setInvSaveErr('');
                          try {
                            const payload = {
                              userId: contact?.userId || '6897acb7f2bc1479c645102d',
                              businessId: contact?.businessId || '6897b362dce30c2c9892b759',
                              contactId: id,
                              type: invForm.type,
                              amount: parseFloat(invForm.amount),
                              currency: 'BDT',
                              date: invForm.date || undefined,
                              description: invForm.description || ''
                            };
                            const res = await API.post('invoice/invoice', payload);
                            const created = res?.data?.result;
                            if (created) setInvoices(prev => [created, ...prev]);
                            setShowNewInv(false);
                            setInvForm({ type: 'income', amount: '', date: '', description: '' });
                          } catch (e) {
                            setInvSaveErr('Failed to save transaction');
                          } finally {
                            setInvSaving(false);
                          }
                        }}
                      >
                        {invSaving ? 'Saving...' : 'Save Transaction'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {invError && <div className="text-danger mb-2">{invError}</div>}
              {(!invoices || invoices.length === 0) ? (
                <div className="text-muted">No transactions for this contact.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-sm align-middle">
                    <thead>
                      <tr>
                        <th style={{width:'20%'}}>Date</th>
                        <th style={{width:'10%'}}>Type</th>
                        <th style={{width:'15%'}} className="text-end">Amount</th>
                        <th>Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {invoices.map(inv => (
                        <tr key={inv._id}>
                          <td>{formatDate(inv.date || inv.createdAt)}</td>
                          <td>
                            <span className={inv.type === 'income' ? 'badge bg-success' : 'badge bg-danger'}>
                              {inv.type}
                            </span>
                          </td>
                          <td className="text-end">{inv.type === 'expense' ? '-' : ''}{Number(inv.amount || 0).toLocaleString()}</td>
                          <td style={{whiteSpace:'pre-wrap'}}>{inv.description || ''}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


 */}
 {/* Upload Files Modal */}
    <Modal
      show={showUpload}
      onClose={()=> { if (!isUploading) { setShowUpload(false); setSelectedFiles([]);} }}
      title="Upload Files"
      body={
        <div>
          <div className="mb-3">
            <input type="file" className="form-control" multiple onChange={handleFileInput} />
            <div className="form-text">Select one or more files for this contact.</div>
          </div>
          {selectedFiles.length > 0 && (
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th>File</th>
                    <th className="text-end">Size</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedFiles.map((f, i) => (
                    <tr key={i}>
                      <td>{f.name}</td>
                      <td className="text-end">{formatSize(f.size)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      }
      footer={
        <>
          <button className="btn btn-secondary" onClick={()=> setShowUpload(false)} disabled={isUploading}>Close</button>
          <button className="btn btn-primary" onClick={handleUpload} disabled={isUploading || selectedFiles.length === 0}>
            {isUploading ? (<><Loader size="sm" className="me-2" />Uploading...</>) : 'Upload'}
          </button>
        </>
      }
    />

    {/* Preview Modal */}
    <Modal
      show={preview.open}
      onClose={() => setPreview({ open: false, url: '', type: '', name: '' })}
      title={`Preview${preview.name ? ` - ${preview.name}` : ''}`}
      dialogClassName="modal-xl"
      body={
        <div>
          {isPreviewLoading && <Loader />}
          {!isPreviewLoading && (
            preview.url ? (
              preview.type === 'image' ? (
                <div className="text-center">
                  <img src={preview.url} alt={preview.name} style={{ maxWidth: '100%', maxHeight: '70vh' }} />
                </div>
              ) : preview.type === 'pdf' ? (
                <iframe title="preview" src={preview.url} style={{ width: '100%', minHeight: '70vh', border: 0 }} />
              ) : (
                <div className="text-center text-muted">Preview not available for this file type.</div>
              )
            ) : (
              <div className="text-center text-muted">Preview not available.</div>
            )
          )}
        </div>
      }
      footer={
        <>
          <button className="btn btn-secondary" onClick={() => setPreview({ open: false, url: '', type: '', name: '' })}>Close</button>
          {preview.url && (
            <button className="btn btn-primary" onClick={() => {
              const link = document.createElement('a');
              link.href = preview.url;
              link.target = '_blank';
              link.rel = 'noopener';
              link.download = preview.name || 'download';
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}>Download</button>
          )}
        </>
      }
    />

    {/* Edit File Modal */}
    <Modal
      show={isEditOpen}
      onClose={()=> setIsEditOpen(false)}
      title="Edit File"
      body={
        <div>
          <div className="mb-3">
            <label htmlFor="editFileName" className="form-label">File name</label>
            <input
              id="editFileName"
              className="form-control"
              value={editDraft.fileName}
              onChange={(e)=> setEditDraft(prev => ({ ...prev, fileName: e.target.value }))}
            />
          </div>
        </div>
      }
      footer={
        <>
          <button className="btn btn-secondary" onClick={()=> setIsEditOpen(false)}>Close</button>
          <button className="btn btn-primary" onClick={saveEdit}>Save Changes</button>
        </>
      }
    />
    <FileUploadModal
      show={showInvoicePhotoModal}
      onClose={()=> setShowInvoicePhotoModal(false)}
      title="Attach Invoice Photos"
      helperText="Choose one or more images of the invoice."
      accept="image/*"
      selectedFiles={pendingInvoicePhotos}
      onFilesChange={setPendingInvoicePhotos}
      primaryActionLabel="Done"
      primaryDisabled={false}
      onPrimaryAction={()=> setShowInvoicePhotoModal(false)}
    />
    </div>
  );
}
